import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemText,
  ListItem,
  ListItemIcon,
  List,
  Container,
  Chip,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import dayjs from 'dayjs';

export interface ReleaseNote {
  version: string;
  releaseDate: string;
  notes: string[];
}

export const releaseNotes: ReleaseNote[] = [
  {
    version: '0.1.13',
    releaseDate: '2025-08-21',
    notes: [
      'Updated Newsfeed links to go to the new Newsfeed pages.',
      'Updated UI/UX throughout the app around tables and other minor updates.',
      'Updated links on the dashboard and added Contact Us link.',
    ],
  },
  {
    version: '0.1.12',
    releaseDate: '2025-08-20',
    notes: [
      'Changed some items around in navigation menu to make it more user friendly. Member Search is now part of the Chapters/Groups, collapsed by default Resources, and renamed Philanthropy to Projects / Foundation.',
      'Added Link PSA.',
      'Updated Release Notes and Modal to be more consistent with the rest of the app.',
    ],
  },
  {
    version: '0.1.11',
    releaseDate: '2025-08-18',
    notes: [
      'Added Shop links to the current Shop page while we wait for the new shop to be ready.',
      'Updated Project Instructions to have updated links.',
      'Fixed an issue on local chapters when trying to update a officer position.',
      'Minor UI / UX updates throughout the app.',
    ],
  },
  {
    version: '0.1.10',
    releaseDate: '2025-08-15',
    notes: ['Updated STAR page to have new recommendation form link.'],
  },
  {
    version: '0.1.9',
    releaseDate: '2025-08-13',
    notes: [
      'Updated the Local Chapter Info Tab to allow information and meeting times to be updated.',
      'Update Foundation and PCE pages to allow for file uploads and style with proper colors.',
      'Reports can now be filtered by columns as well as sorting and filtering.',
      'Updated the Reset Password page to allow optional visibility of the password selected.',
      'Updated to where LC Officers can update Member Information of inactive members in their chapter.',
    ],
  },
  {
    version: '0.1.8',
    releaseDate: '2025-08-08',
    notes: [
      'Added Cell Phone to Add Member page.',
      'Allow the ability to update Reciprocity and Groups.',
      'Added the Type field to Reciprocity and Groups to show which it is.',
      'Added filter to Reciprocity and Groups to show all or just Reciprocity or Group.',
      'Added the ability to update TLCs and their members.',
      'Added the ability to add a new member to a TLC.',
      'Added the ability to unlock accounts on login for the International website.',
      'Add Link to SPD Membership page to quick links.',
      'Fixed an issue where if the country was missing from an address, updating the address would not work.',
    ],
  },
  {
    version: '0.1.7',
    releaseDate: '2025-08-06',
    notes: [
      'Fixed an issue were some Local Chapter Officers were not able to see the Member Information tab.',
      'Updated wording on some links and buttons in the projects pages.',
      'Added clarification to the reset password page.',
      'Fixed an issue where permissions were blocking searching in Bed and Breakfast listings.',
      'Continued work on single sign on and links to international website. There are still some items being worked on but should be working for most users.',
    ],
  },
  {
    version: '0.1.6',
    releaseDate: '2025-08-06',
    notes: [
      'Added new Projects page to allow for easy access to all projects.',
      'Added new Cottey College page to allow for easy access to Cottey College information.',
      'Added new PCE page to allow for easy access to PCE information.',
      'Added new IPS page to allow for easy access to IPS information.',
      'Added new PSA page to allow for easy access to PSA information.',
      'Added new STAR page to allow for easy access to STAR information.',
      'Added new Foundation page to allow for easy access to Foundation information.',
      'Fixed issue with pagination on Library page.',
      'Updated Login process to fix redirects to the International page. (If you are having issues with links still not working, please log out and log back in.)',
      'Updated Drawer to have new projects and resources.',
    ],
  },
  {
    version: '0.1.5',
    releaseDate: '2025-08-03',
    notes: [
      'Added ability to view Local Chapter Resources.',
      'Fixed issue on Local Chapter Leadership when position was vacant it would error trying to add.',
      'Added International Info, Committees, Board, and Trustees.',
      'Added ability to edit member information.',
      'Filtered results for non-member projects to only display desired projects.',
      'Reciprocity Positions have been explanded to include all potential positions.',
      'Updated quick links on the dashboard to allow easy transfer for new pages and intenational website.',
      'New Messaging system to allow for conversations between members and leadership.',
      'Added new PI information (Is Active) to the User Detail page.',
      'Added new items search will look for (email, primary phone, cell phone).',
      'Added new forgot password workflows.',
      'Added new SPD Resources page.',
      'Updated Resources Library to have lots of new resources.',
    ],
  },
];

const formatReleaseDate = (dateString: string): string => {
  try {
    return dayjs(dateString).format('MMMM D, YYYY');
  } catch {
    // Fallback to original format if parsing fails
    return dateString;
  }
};

const ReleaseNotes: React.FC = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h3"
          component="h1"
          color="primary"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 2,
          }}
        >
          Release Notes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Stay updated with the latest features and improvements
        </Typography>
      </Box>

      <Box>
        {releaseNotes.map((releaseNote, index) => (
          <Accordion
            key={`${releaseNote.version}-${index}`}
            sx={{
              mb: 2,
              '&:before': {
                display: 'none',
              },
              boxShadow: theme.shadows[2],
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 2,
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  label={`v${releaseNote.version}`}
                  color="secondary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {formatReleaseDate(releaseNote.releaseDate)}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                backgroundColor: theme.palette.background.paper,
                p: 3,
              }}
            >
              <List sx={{ p: 0 }}>
                {releaseNote.notes.map((note, noteIndex) => (
                  <ListItem
                    key={`${releaseNote.version}-note-${noteIndex}`}
                    sx={{
                      px: 0,
                      py: 0.5,
                      '&:not(:last-child)': {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 1,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TrackChangesIcon color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={note}
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { lineHeight: 1.6 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Container>
  );
};

export default ReleaseNotes;
